import type { TokenStore } from '@/lib/api';

/**
 * The refresh token store for native builds.
 *
 * ## Why this is not simply `await SecureStorage.get(...)`
 *
 * `TokenStore` is synchronous, and has to be: `ApiClient.send()` reads the access token while
 * assembling headers, and `hasSession` is consulted inside a router guard. The Keychain and
 * the Android Keystore are both asynchronous. Rather than make every caller `await` — which
 * would turn the guard, the header assembly and the single-flight refresh into async paths for
 * the benefit of one platform — the token is read **once** at boot into memory, and every
 * subsequent write is mirrored back to the keychain without being awaited.
 *
 * The consequence, stated plainly: if the OS kills the app in the few milliseconds between
 * `setRefresh()` and the keychain write landing, the device keeps the *previous* token. That
 * token has already been spent, so presenting it triggers the server's reuse detection, the
 * token family is revoked and the user signs in again. That is the correct failure — a
 * rotation gap must fail closed — and it is why `persist()` logs rather than throws.
 *
 * ## What is stored, and what is not
 *
 * Only the refresh token. The access token stays in memory in every transport: it lives 15
 * minutes and persisting it buys nothing but a second thing to steal. `sessionHint` is
 * meaningless here — on mobile the app can see its own refresh token, so `hasSession` asks
 * `getRefresh()` directly (see `ApiClient.hasSession`).
 */

/** The slice of the plugin this needs. Narrow, so tests supply a fake without a device. */
export interface SecureBackend {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export const REFRESH_KEY = 'sag.refresh';

export interface SecureTokenStore extends TokenStore {
  /** Reads the keychain into memory. Must resolve before the first request is sent. */
  hydrate(): Promise<void>;
  /** Resolves when every queued keychain write has settled. Tests await it; the app does not. */
  settled(): Promise<void>;
}

export function secureTokenStore(
  backend: SecureBackend,
  onError: (err: unknown, op: string) => void = () => undefined,
): SecureTokenStore {
  let access: string | null = null;
  let refresh: string | null = null;

  /**
   * Writes are serialised through one chain, not fired in parallel.
   *
   * Two `setRefresh()` calls racing on the keychain can land in either order, and the loser
   * overwrites the newer token with an older one — which, on the next refresh, is a spent
   * token and a revoked family. Chaining makes the last call win, which is the intent.
   */
  let queue: Promise<void> = Promise.resolve();

  const persist = (op: string, run: () => Promise<void>): void => {
    queue = queue.then(run).catch((err: unknown) => {
      // A keychain failure must not take the session down with it: the token is already in
      // memory and this run of the app works. Only the *next* cold start suffers.
      onError(err, op);
    });
  };

  return {
    async hydrate() {
      try {
        refresh = await backend.get(REFRESH_KEY);
      } catch (err) {
        onError(err, 'hydrate');
        refresh = null;
      }
    },

    settled: () => queue,

    getAccess: () => access,
    setAccess: (token) => void (access = token),

    getRefresh: () => refresh,
    setRefresh(token) {
      refresh = token;
      if (token === null) persist('remove', () => backend.remove(REFRESH_KEY));
      else persist('set', () => backend.set(REFRESH_KEY, token));
    },

    // On native the refresh token is readable by the app, so the hint is the token itself.
    // Persisting a separate boolean would only let the two disagree.
    hasSessionHint: () => refresh !== null,
    setSessionHint: () => undefined,

    clear() {
      access = null;
      this.setRefresh(null);
    },
  };
}
